import * as React from "react";
import {
  EE, GB, RU, LV, LT, FI, SE, DE, UA, FR, ES, PL, NL, IT, PT,
  DK, NO, CZ, SK, HU, RO, BG, GR, TR, SA, IL, JP, KR, CN, IN, TH, VN, ID,
} from "country-flag-icons/react/3x2";
import { cn } from "../lib/utils";

/**
 * Locale → country flag as an SVG (ISO 3166-1 alpha-2). Replaces the emoji flags that
 * are inconsistent across platforms (regional-indicator emojis are not rendered by Windows
 * or some Linuxes). The mapping follows the earlier emoji choice: en → GB, uk → UA,
 * sv → SE etc. Unknown locale → a neutral placeholder so the layout does not jump.
 *
 * Use it everywhere `localeFlag()` used to be - one source of truth across the whole platform.
 */
const LOCALE_COUNTRY: Record<string, typeof EE> = {
  et: EE, en: GB, ru: RU, lv: LV, lt: LT, fi: FI,
  sv: SE, de: DE, uk: UA, fr: FR, es: ES,
  pl: PL, nl: NL, it: IT, pt: PT,
  // Extension: the platform's language catalog is dynamic (a superadmin may add
  // any ISO 639-1 language) - cover the common languages; unknown → placeholder.
  da: DK, no: NO, nb: NO, cs: CZ, sk: SK, hu: HU, ro: RO, bg: BG, el: GR,
  tr: TR, ar: SA, he: IL, ja: JP, ko: KR, zh: CN, hi: IN, th: TH, vi: VN, id: ID,
};

export function LocaleFlag({
  locale,
  className,
  title,
}: {
  locale: string;
  /** The width is set here (e.g. `w-4`, `w-5`); the height comes from the 3:2 aspect ratio. Default `w-3.5` (14px). */
  className?: string;
  title?: string;
}) {
  const Flag = LOCALE_COUNTRY[locale];
  // Default width w-3.5 (14px) - proportional next to small text; the caller
  // can override it with `className` (e.g. `w-4` next to larger text).
  if (!Flag) {
    return (
      <span
        aria-hidden
        className={cn("inline-block w-3.5 rounded-[2px] text-center leading-none", className)}
      >
        🏳
      </span>
    );
  }
  return (
    <Flag
      title={title}
      aria-hidden={title ? undefined : true}
      className={cn("inline-block h-auto w-3.5 rounded-[2px]", className)}
    />
  );
}
